import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

const useBookReviews = (bookId) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch reviews for a specific book
  const fetchReviews = useCallback(async () => {
    if (!bookId) return;
    
    setLoading(true);
    setError(null);
    try {
      const reviewsRef = collection(db, 'books', bookId, 'reviews');
      const reviewsQuery = query(reviewsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(reviewsQuery);
      
      const reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setReviews(reviewsData);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message);
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Add a new review
  const addReview = useCallback(async (reviewData) => {
    if (!bookId) {
      toast.error('Book ID is required');
      return null;
    }

    try {
      const reviewsRef = collection(db, 'books', bookId, 'reviews');
      const newReview = {
        ...reviewData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(reviewsRef, newReview);
      const reviewWithId = { id: docRef.id, ...newReview };
      
      // Update local state
      setReviews(prev => [reviewWithId, ...prev]);
      
      toast.success('Review added successfully');
      return reviewWithId;
    } catch (err) {
      console.error('Error adding review:', err);
      toast.error('Failed to add review');
      throw err;
    }
  }, [bookId]);

  // Update an existing review
  const updateReview = useCallback(async (reviewId, updateData) => {
    if (!bookId) {
      toast.error('Book ID is required');
      return false;
    }

    try {
      const reviewRef = doc(db, 'books', bookId, 'reviews', reviewId);
      const updatePayload = {
        ...updateData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(reviewRef, updatePayload);
      
      // Update local state
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, ...updatePayload }
          : review
      ));
      
      toast.success('Review updated successfully');
      return true;
    } catch (err) {
      console.error('Error updating review:', err);
      toast.error('Failed to update review');
      throw err;
    }
  }, [bookId]);

  // Delete a review
  const deleteReview = useCallback(async (reviewId) => {
    if (!bookId) {
      toast.error('Book ID is required');
      return false;
    }

    try {
      const reviewRef = doc(db, 'books', bookId, 'reviews', reviewId);
      await deleteDoc(reviewRef);
      
      // Update local state
      setReviews(prev => prev.filter(review => review.id !== reviewId));
      
      toast.success('Review deleted successfully');
      return true;
    } catch (err) {
      console.error('Error deleting review:', err);
      toast.error('Failed to delete review');
      throw err;
    }
  }, [bookId]);

  // Delete multiple reviews
  const deleteMultipleReviews = useCallback(async (reviewIds) => {
    if (!bookId) {
      toast.error('Book ID is required');
      return false;
    }

    try {
      const batch = writeBatch(db);
      
      reviewIds.forEach(reviewId => {
        const reviewRef = doc(db, 'books', bookId, 'reviews', reviewId);
        batch.delete(reviewRef);
      });
      
      await batch.commit();
      
      // Update local state
      setReviews(prev => prev.filter(review => !reviewIds.includes(review.id)));
      
      toast.success(`${reviewIds.length} review(s) deleted successfully`);
      return true;
    } catch (err) {
      console.error('Error deleting multiple reviews:', err);
      toast.error('Failed to delete reviews');
      throw err;
    }
  }, [bookId]);

  // Get review statistics
  const getReviewStats = useCallback(() => {
    if (reviews.length === 0) {
      return {
        total: 0,
        averageRating: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      };
    }

    const total = reviews.length;
    const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0);
    const averageRating = totalRating / total;

    const ratingDistribution = reviews.reduce((acc, review) => {
      const rating = review.rating || 0;
      acc[rating] = (acc[rating] || 0) + 1;
      return acc;
    }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

    return {
      total,
      averageRating: Math.round(averageRating * 10) / 10,
      ratingDistribution
    };
  }, [reviews]);

  // Refresh reviews
  const refreshReviews = useCallback(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Load reviews when bookId changes
  useEffect(() => {
    if (bookId) {
      fetchReviews();
    } else {
      setReviews([]);
    }
  }, [bookId, fetchReviews]);

  return {
    reviews,
    loading,
    error,
    addReview,
    updateReview,
    deleteReview,
    deleteMultipleReviews,
    getReviewStats,
    refreshReviews
  };
};

export default useBookReviews;
